<script>
/*
Explota una celda del reporte de Resultados por Tesorería: pide al backend los aportes que
componen el importe (concepto + columna) y los muestra en el ModalTable. Desde el Nº de
movimiento se abre el movimiento de tesorería original, igual que en el balance por cuenta, con
la misma edición acotada que la grilla de Movimientos de Tesorería, y desde la última columna se
reasigna el concepto del ítem que originó el aporte.

Las filas del bloque de Disponibilidades no salen del prorrateo: su detalle son las patas del
movimiento sobre esa cuenta, que se piden al balance por cuenta. onCellClick() despacha entre
ambos detalles según el tipo de fila que emite TablaResultados.

La vista que lo use debe implementar getFilterParams() y filter(), y exponer conceptos y
cost_centers para el diálogo de reasignación. Si muestra el bloque de Disponibilidades
(by_blocks), además debe implementar getRangoCuenta(cell).
*/
import Constant from "@/constants/sections";
import { generalService } from "@/libs/ws/generalService";
import { tesoreriaMovementService } from "@/libs/ws/tesoreriaMovementService";

export default {
  name: "ResultadosDetalleMixin",
  data() {
    return {
      tesoreriaMovementService: tesoreriaMovementService,
      open_detalle: false,
      loading_detalle: false,
      detalle_items: [],
      detalle_title: "",
      detalle_headers: [],
      detalle_headers_concepto: [
        { text: "Fecha", align: "left", sortable: true, value: "date", format: "date" },
        { text: "Nº Mov.", align: "left", sortable: true, value: "nro_mov", clickable: true },
        { text: "Clase", align: "left", sortable: true, value: "clase" },
        { text: "Origen", align: "left", sortable: true, value: "origen" },
        { text: "Detalle", align: "left", sortable: true, value: "descripcion" },
        { text: "Centro de costo", align: "left", sortable: true, value: "cost_center" },
        { text: "Importe", align: "right", sortable: true, value: "amount", format: "price", total: true },
        { text: "", align: "left", sortable: false, value: "reasignar", clickable: true },
      ],
      // Las filas de cuenta no salen del prorrateo: su detalle son las patas del movimiento.
      detalle_headers_cuenta: [
        { text: "Fecha", align: "left", sortable: true, value: "date", format: "date" },
        { text: "Nº Mov.", align: "left", sortable: true, value: "nro_mov", clickable: true },
        { text: "Clase", align: "left", sortable: true, value: "clase" },
        { text: "Detalle", align: "left", sortable: true, value: "descripcion" },
        { text: "Importe", align: "right", sortable: true, value: "amount", format: "price", total: true },
      ],
      dialog_movimiento: false,
      movimiento_item: {},
      dialog_reasignar: false,
      reasignar_item: {},
      detalle_cell: null,
    };
  },
  created() {
    this.detalle_headers = this.detalle_headers_concepto;
  },
  computed: {
    // Mismo criterio que la grilla de Movimientos de Tesoreria: la edicion acotada (concepto,
    // referencia y motivo de cada linea) solo aplica a movimientos manuales. El permiso es el del
    // modulo de tesoreria, no el del reporte. Sin permiso el modal queda en modo ver.
    movimiento_formBtn() {
      if (!this.movimiento_item.is_manual) return "";
      if (!this.getModuleAccess(Constant.SECTION_TESORERIA_MOVEMENTS, "update")) return "";
      return "Editar";
    },
  },
  methods: {
    // TablaResultados emite un solo cell-click para toda la matriz: el bloque de origen define
    // que detalle se abre.
    onCellClick(cell) {
      this.detalle_cell = cell;
      if (cell.tipo_fila === "cuenta") {
        this.detalle_headers = this.detalle_headers_cuenta;
        this.openDetalleCuenta(cell);
        return;
      }
      this.detalle_headers = this.detalle_headers_concepto;
      this.openDetalle(cell);
    },
    // Detalle de una celda de Disponibilidades: las patas de esa cuenta en el período de la
    // columna, sin prorrateo. El rango lo aporta la vista, que es la que conoce la apertura.
    openDetalleCuenta(cell) {
      const rango = this.getRangoCuenta(cell);

      this.detalle_title = cell.concepto_name + " - " + cell.columna_name;
      this.detalle_items = [];
      this.open_detalle = true;
      this.loading_detalle = true;

      tesoreriaMovementService
        .balanceByAccount({ money_account_id: cell.concepto_id, date_from: rango[0], date_to: rango[1] })
        .then((response) => {
          if (!response.status) {
            this.$toast.error(response.msg, "Error");
            return;
          }
          this.detalle_items = response.result.details.map((d) => ({
            movement_id: d.tesoreria_movement.id,
            date: d.tesoreria_movement.date,
            nro_mov: d.tesoreria_movement.nro_mov,
            clase: d.tesoreria_movement.clase_comprobante ? d.tesoreria_movement.clase_comprobante.name : "",
            descripcion: d.tesoreria_movement.concept || d.tesoreria_movement.reference || "",
            amount: d.movement_type === "D" ? parseFloat(d.amount) : -parseFloat(d.amount),
          }));
        })
        .catch((e) => console.log(e))
        .finally(() => (this.loading_detalle = false));
    },
    openDetalle(cell) {
      const param = this.getFilterParams();
      param.concepto = cell.concepto_id;
      param.columna = cell.columna_id;

      this.detalle_title = cell.concepto_name + " - " + cell.columna_name;
      this.detalle_items = [];
      this.open_detalle = true;
      this.loading_detalle = true;

      generalService
        .query(param, Constant.SECTION_REPORT_RESULTADOS_DETALLE)
        .then((response) => {
          // Solo se reimputan los aportes con un origen editable: el ítem de un comprobante o la
          // línea de un movimiento manual. Los saldos a cuenta y las líneas que arma el sistema
          // (cobros, pagos, cheques) no traen item_id.
          this.detalle_items = response.result.map((item) => ({
            ...item,
            concepto_name: cell.concepto_name,
            reasignar: item.item_id ? "Reasignar" : "",
          }));
        })
        .catch((e) => console.log(e))
        .finally(() => (this.loading_detalle = false));
    },
    closeDetalle() {
      this.open_detalle = false;
      this.detalle_items = [];
      this.detalle_cell = null;
    },
    // El detalle del movimiento llega con movement_type; el modal espera debe/haber separados.
    normalizeDetails(item) {
      if (item.details) {
        item.details = item.details.map((d) => ({
          ...d,
          amount_debe: d.movement_type === "D" ? d.amount : null,
          amount_haber: d.movement_type === "H" ? d.amount : null,
        }));
      }
      return item;
    },
    // La tabla del detalle emite un solo cell-click: se despacha por la columna clickeada.
    onDetalleCellClick(cell) {
      if (cell.column.value === "reasignar") return this.openReasignar(cell.item);
      return this.openMovimiento(cell);
    },
    openReasignar(item) {
      if (!item.item_id) return;
      this.reasignar_item = item;
      this.dialog_reasignar = true;
    },
    closeReasignar() {
      this.dialog_reasignar = false;
      this.reasignar_item = {};
    },
    // Tras reasignar, la celda de origen ya no contiene ese aporte: se recarga el reporte y se
    // cierra el detalle, que quedó desactualizado.
    onReasignado() {
      this.closeReasignar();
      this.closeDetalle();
      this.filter();
    },
    openMovimiento(cell) {
      if (cell.item.movement_id == null) return;
      this.loading_detalle = true;
      tesoreriaMovementService
        .get({ id: cell.item.movement_id })
        .then((r) => {
          if (r.status) {
            this.movimiento_item = this.normalizeDetails(JSON.parse(JSON.stringify(r.result)));
            this.dialog_movimiento = true;
          } else {
            this.$toast.error(r.msg || "Error al cargar el movimiento.", "Error");
          }
        })
        .catch((e) => {
          this.$toast.error((e && e.message) || "Error al cargar el movimiento.", "Error");
        })
        .finally(() => (this.loading_detalle = false));
    },
    updateDialogMovimiento(val) {
      this.dialog_movimiento = val;
    },
    // La edicion solo toca textos: los importes del reporte no cambian, pero la columna Detalle
    // del listado sale del concepto/referencia del movimiento, asi que se recarga el detalle.
    onMovimientoGuardado() {
      if (this.detalle_cell != null) this.onCellClick(this.detalle_cell);
    },
  },
};
</script>
